/**
 * MaskedVinBadge — a bordered mono stamp presenting the partially-masked VIN
 * from a public dossier payload.
 *
 * Design spec: NOT a Pill, NOT a StampChip. A rectangular bordered block
 * using F.mono / tabular-nums, with a "Chassis identity (partially masked)"
 * sub-label. The server renders the mask string verbatim — the client
 * never applies masking, just renders whatever the server returned.
 *
 * Accessibility: aria-label describes the content as "chassis identity,
 * partially masked" so screen readers don't attempt to read individual mask
 * glyphs as characters. The mask glyphs ("…", "X", etc.) are aria-hidden.
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No Web APIs without Capacitor equivalents.
 */

import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';

export interface MaskedVinBadgeProps {
  /**
   * Server-rendered masked VIN string, e.g. "…123456" or "WDB201*****123456".
   * Rendered verbatim — no client-side masking.
   */
  vinPartial?: string | undefined;
  /**
   * Stable badge display string. If present, takes display priority over
   * vinPartial. Currently the same as vinPartial in the API; reserved for
   * future formatting differences.
   */
  vinBadge?: string | undefined;
}

export function MaskedVinBadge({ vinPartial, vinBadge }: MaskedVinBadgeProps) {
  const displayString = vinBadge ?? vinPartial;

  if (!displayString) return null;

  return (
    <div
      style={{
        display: 'inline-block',
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.sm,
        padding: `${SPACE.xs}px ${SPACE.sm}px`,
        background: T.card,
      }}
      // Screen readers must not attempt to read mask glyphs character by character.
      aria-label={`VIN: chassis identity, partially masked. Displayed as: ${displayString}`}
      role="img"
    >
      {/* Main VIN string — aria-hidden because the aria-label above covers it */}
      <div
        aria-hidden="true"
        style={{
          fontFamily: F.mono,
          fontVariantNumeric: 'tabular-nums',
          fontSize: 15,
          fontWeight: 600,
          color: T.text,
          letterSpacing: '0.04em',
          lineHeight: 1.3,
        }}
      >
        {displayString}
      </div>
      {/* Sub-label */}
      <div
        aria-hidden="true"
        style={{
          marginTop: SPACE.xs,
        }}
      >
        <span
          style={{
            fontFamily: F.mono,
            fontSize: 10,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: T.textMuted,
          }}
        >
          VIN (partially masked)
        </span>
      </div>
    </div>
  );
}
